class ProductCustomizer extends HTMLElement {
  connectedCallback() {
    this.maxFileSize = parseInt(this.dataset.maxFileSize || '10485760', 10);
    this.currency = this.dataset.currency || '£';
    this.productTitle = this.dataset.productTitle || '';

    this.optionSelects = this.querySelectorAll('[data-option-select]');
    this.logoFeeEl = this.querySelector('[data-logo-fee]');
    this.logoNote = this.querySelector('[data-logo-note]');
    this.runningTotalRow = this.querySelector('[data-running-total]');
    this.runningTotalValue = this.querySelector('[data-running-total-value]');
    this.addToCartButton = this.querySelector('[data-customizer-add-to-cart]');
    this.submitError = this.querySelector('[data-submit-error]');
    this.locationsContainer = this.querySelector('[data-locations]');
    this.locationsEmpty = this.querySelector('[data-locations-empty]');
    this.locationTemplate = this.querySelector('[data-location-template]');
    this.comboRules = Array.from(this.querySelectorAll('[data-combo-rule]')).map((el) => ({
      optionId: el.dataset.comboOptionId,
      comboOf: el.dataset.comboOf.split(','),
      price: parseFloat(el.dataset.comboPrice || '0'),
      feeVariantId: el.dataset.comboFeeVariantId,
    }));

    // One customizer card per selected position, keyed by option id.
    this.locations = new Map();

    // Variant ids of order-wide (POR_PEDIDO) fee lines already in the cart.
    this.cartOrderFeeVariantIds = new Set();
    this.refreshCartOrderFees().then(() => this.updateRunningTotal());

    this.optionSelects.forEach((select) => {
      select.addEventListener('change', () => this.sync());

      // Progressively enhance multi-select groups (Front/Sleeves/Back) into a
      // normal-looking dropdown that turns picks into removable pills.
      if (select.multiple && 'multiPills' in select.dataset) {
        this.initChoices(select);
      }
    });

    this.sync();

    this.form = this.closest('form');
    if (this.addToCartButton) {
      this.addToCartButton.addEventListener('click', (e) => this.handleAddToCart(e));
    }
  }

  initChoices(select, attempt = 0) {
    if (window.Choices) {
      const choices = new window.Choices(select, {
        removeItemButton: true,
        shouldSort: false,
        // Search must stay enabled: Choices only renders its placeholder text
        // (via the same input element used for search) when search is on.
        searchEnabled: true,
        searchPlaceholderValue: 'Search…',
        placeholder: true,
        placeholderValue: 'Choose a position…',
        itemSelectText: '',
        duplicateItemsAllowed: false,
      });

      // "Not required" is exclusive: picking it clears the positions, picking a position clears it.
      select.addEventListener('addItem', (e) => {
        const isNone = e.detail.value === 'none';
        choices.getValue().forEach((item) => {
          if ((item.value === 'none') !== isNone) choices.removeActiveItemsByValue(item.value);
        });
      });
      return;
    }
    // The Choices library loads via a plain <script> tag above this one; if it
    // hasn't finished evaluating yet, retry briefly instead of silently falling
    // back to the native multi-select.
    if (attempt < 20) {
      setTimeout(() => this.initChoices(select, attempt + 1), 100);
    }
  }

  sync() {
    this.syncLocations();
    this.updateRunningTotal();
  }

  // ---------- Positions ----------

  // Every currently selected option, in the order the selects are rendered.
  // Titles that repeat across groups (e.g. "Left" under both Sleeves and Back)
  // get prefixed with their group so cart properties stay distinguishable.
  getSelectedPositions() {
    const positions = [];

    this.optionSelects.forEach((select) => {
      Array.from(select.selectedOptions).forEach((option) => {
        if (!option.value || 'none' in option.dataset) return;
        const title = (option.dataset.title || option.textContent || '').trim();
        // An option can ask for several designs (metaobject "Design count"), e.g. Stanley "Two sides".
        const count = Math.max(1, parseInt(option.dataset.designCount || '1', 10));
        for (let n = 1; n <= count; n++) {
          positions.push({
            id: count > 1 ? `${option.value}#${n}` : option.value,
            title: count > 1 ? `${title} (${n}/${count})` : title,
            group: (option.dataset.group || '').trim(),
          });
        }
      });
    });

    const titleCounts = {};
    positions.forEach((pos) => {
      titleCounts[pos.title] = (titleCounts[pos.title] || 0) + 1;
    });
    positions.forEach((pos) => {
      pos.label = titleCounts[pos.title] > 1 && pos.group ? `${pos.group} – ${pos.title}` : pos.title;
    });

    return positions;
  }

  syncLocations() {
    if (!this.locationsContainer || !this.locationTemplate) return;

    const positions = this.getSelectedPositions();
    const selectedIds = new Set(positions.map((pos) => pos.id));

    this.locations.forEach((location, id) => {
      if (!selectedIds.has(id)) {
        this.destroyLocation(location);
        this.locations.delete(id);
      }
    });

    positions.forEach((position) => {
      let location = this.locations.get(position.id);
      if (!location) {
        location = this.createLocation();
        this.locations.set(position.id, location);
      }
      location.position = position;
      location.titleEl.textContent = position.label;
      // appendChild also re-orders existing cards to match the selects.
      this.locationsContainer.appendChild(location.el);
      this.applyInputNames(location);
    });

    this.refreshSameAs(positions);

    const hasPositions = positions.length > 0;
    this.locationsContainer.hidden = !hasPositions;
    if (this.locationsEmpty) this.locationsEmpty.hidden = hasPositions;
    if (this.logoNote) this.logoNote.hidden = !this.setupFeeSelected();
  }

  createLocation() {
    const el = this.locationTemplate.content.firstElementChild.cloneNode(true);
    const location = {
      el,
      position: null,
      sameAs: false,
      hasFile: false,
      previewObjectUrl: null,
      titleEl: el.querySelector('[data-location-title]'),
      body: el.querySelector('[data-location-body]'),
      sameAsWrapper: el.querySelector('[data-same-as]'),
      sameAsToggle: el.querySelector('[data-same-as-toggle]'),
      sameAsSelect: el.querySelector('[data-same-as-select]'),
      sameAsValue: el.querySelector('[data-same-as-value]'),
      tabs: el.querySelectorAll('[data-tab]'),
      panels: el.querySelectorAll('[data-panel]'),
      dropzone: el.querySelector('[data-dropzone]'),
      fileInput: el.querySelector('[data-file-input]'),
      dropzoneText: el.querySelector('[data-dropzone-text]'),
      dropzoneIcon: el.querySelector('[data-dropzone-icon]'),
      dropzonePreview: el.querySelector('[data-dropzone-preview]'),
      fileError: el.querySelector('[data-file-error]'),
      textInput: el.querySelector('[data-text-input]'),
    };

    location.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.switchTab(location, tab.dataset.tab));
    });

    ['dragenter', 'dragover'].forEach((evt) => {
      location.dropzone.addEventListener(evt, (e) => {
        e.preventDefault();
        location.dropzone.classList.add('product-customizer__dropzone--dragover');
      });
    });

    ['dragleave', 'drop'].forEach((evt) => {
      location.dropzone.addEventListener(evt, (e) => {
        e.preventDefault();
        location.dropzone.classList.remove('product-customizer__dropzone--dragover');
      });
    });

    location.dropzone.addEventListener('drop', (e) => {
      const files = e.dataTransfer?.files;
      if (files && files.length) {
        location.fileInput.files = files;
        this.handleFile(location, files[0]);
      }
    });

    location.fileInput.addEventListener('change', () => {
      if (location.fileInput.files.length) {
        this.handleFile(location, location.fileInput.files[0]);
      } else {
        location.hasFile = false;
        this.clearPreview(location);
        this.updateRunningTotal();
      }
    });

    location.sameAsToggle.addEventListener('change', () => {
      this.setSameAs(location, location.sameAsToggle.checked);
      // Toggling changes which positions are valid "same as" targets elsewhere.
      this.syncLocations();
      this.updateRunningTotal();
    });

    location.sameAsSelect.addEventListener('change', () => this.updateSameAsValue(location));

    // Typed text counts as personalization, so the running total must follow it.
    location.textInput.addEventListener('input', () => this.updateRunningTotal());

    return location;
  }

  destroyLocation(location) {
    if (location.previewObjectUrl) URL.revokeObjectURL(location.previewObjectUrl);
    location.el.remove();
  }

  // Property names carry the position so the cart shows one row per position.
  applyInputNames(location) {
    const label = location.position.label;
    location.fileInput.name = `properties[${label} Artwork]`;
    location.textInput.name = `properties[${label} Text]`;
    location.sameAsValue.name = `properties[${label}]`;
    this.updateSameAsValue(location);
  }

  // ---------- "Same design as another position" ----------

  refreshSameAs(positions) {
    positions.forEach((position, index) => {
      const location = this.locations.get(position.id);
      if (!location) return;

      // The first position is always the one that carries a design; the others
      // may point at it (or at any other position that isn't itself pointing).
      const candidates =
        index === 0
          ? []
          : positions.filter((other) => {
              if (other.id === position.id) return false;
              const otherLocation = this.locations.get(other.id);
              return otherLocation && !otherLocation.sameAs;
            });

      if (!candidates.length) {
        location.sameAsWrapper.hidden = true;
        if (location.sameAs) this.setSameAs(location, false);
        return;
      }

      location.sameAsWrapper.hidden = false;

      const previous = location.sameAsSelect.value;
      location.sameAsSelect.textContent = '';
      candidates.forEach((candidate) => {
        const option = document.createElement('option');
        option.value = candidate.id;
        option.textContent = candidate.label;
        location.sameAsSelect.appendChild(option);
      });
      const stillValid = candidates.some((candidate) => candidate.id === previous);
      location.sameAsSelect.value = stillValid ? previous : candidates[0].id;

      this.updateSameAsValue(location);
    });
  }

  setSameAs(location, on) {
    location.sameAs = on;
    location.sameAsToggle.checked = on;
    location.body.hidden = on;
    location.sameAsSelect.hidden = !on;
    // Disabled inputs are skipped by FormData, so the mirrored position submits
    // only its "Same as …" property while keeping any values typed earlier.
    location.fileInput.disabled = on;
    location.textInput.disabled = on;
    location.sameAsValue.disabled = !on;
    this.updateSameAsValue(location);
  }

  updateSameAsValue(location) {
    if (!location.position) return;
    if (!location.sameAs) {
      location.sameAsValue.value = '';
      return;
    }
    const target = this.locations.get(location.sameAsSelect.value);
    location.sameAsValue.value = target ? `Same as ${target.position.label}` : '';
  }

  // ---------- Upload / text ----------

  switchTab(location, target) {
    location.tabs.forEach((tab) => {
      tab.classList.toggle('product-customizer__tab--active', tab.dataset.tab === target);
    });
    location.panels.forEach((panel) => {
      panel.hidden = panel.dataset.panel !== target;
    });
  }

  handleFile(location, file) {
    location.fileError.hidden = true;
    location.fileError.textContent = '';

    const allowedExtensions = ['.png', '.jpg', '.jpeg', '.webp', '.gif', '.svg', '.ai', '.eps', '.pdf'];
    const extension = '.' + file.name.split('.').pop().toLowerCase();

    if (!allowedExtensions.includes(extension)) {
      this.showFileError(location, 'Please upload a PNG, JPG, WebP, GIF, SVG, AI, EPS, or PDF file.');
      location.fileInput.value = '';
      location.hasFile = false;
      this.clearPreview(location);
      this.updateRunningTotal();
      return;
    }

    if (file.size > this.maxFileSize) {
      this.showFileError(location, 'File is too large. Max size is 10MB.');
      location.fileInput.value = '';
      location.hasFile = false;
      this.clearPreview(location);
      this.updateRunningTotal();
      return;
    }

    location.dropzoneText.innerHTML = `<strong>${file.name}</strong><small>Click or drop to replace</small>`;

    // Raster images and SVG can be rendered directly in the browser — show an instant local
    // preview. AI, EPS and PDF files aren't image-renderable, so keep the file icon for those.
    const previewableExtensions = ['.png', '.jpg', '.jpeg', '.webp', '.gif', '.svg'];
    if (location.dropzonePreview && previewableExtensions.includes(extension)) {
      if (location.previewObjectUrl) URL.revokeObjectURL(location.previewObjectUrl);
      location.previewObjectUrl = URL.createObjectURL(file);
      location.dropzonePreview.src = location.previewObjectUrl;
      location.dropzonePreview.hidden = false;
      if (location.dropzoneIcon) location.dropzoneIcon.hidden = true;
    } else {
      this.clearPreview(location);
    }

    location.hasFile = true;
    this.updateRunningTotal();
  }

  clearPreview(location) {
    if (location.previewObjectUrl) {
      URL.revokeObjectURL(location.previewObjectUrl);
      location.previewObjectUrl = null;
    }
    if (location.dropzonePreview) {
      location.dropzonePreview.hidden = true;
      location.dropzonePreview.removeAttribute('src');
    }
    if (location.dropzoneIcon) location.dropzoneIcon.hidden = false;
  }

  showFileError(location, message) {
    location.fileError.hidden = false;
    location.fileError.textContent = message;
  }

  showSubmitError(message) {
    if (!this.submitError) return;
    this.submitError.hidden = false;
    this.submitError.textContent = message;
  }

  // True once any position carries its own artwork or text. "Same design as"
  // positions are ignored: they mirror a position that is counted itself.
  hasPersonalization() {
    let personalized = false;
    this.locations.forEach((location) => {
      if (location.sameAs) return;
      if (location.hasFile || location.textInput.value.trim()) personalized = true;
    });
    return personalized;
  }

  // ---------- Fees ----------

  // True while a chosen option has the metaobject "Adds setup fee" flag.
  setupFeeSelected() {
    return !!this.querySelector('[data-option-select] option[data-adds-setup-fee]:checked');
  }

  // Returns { feeVariantId, price, application, title } for everything actually being
  // charged. Each <select> can have multiple selected options (Front/Sleeves/Back allow
  // more than one position), the combo rule substitutes e.g. Both Sleeves for the two
  // singles, and the logo setup fee auto-applies once artwork or text is added while a
  // chosen option has "Adds setup fee".
  getSelectedFees() {
    const selectedIds = [];
    let fees = [];

    this.optionSelects.forEach((select) => {
      Array.from(select.selectedOptions).forEach((option) => {
        if (!option.value || 'none' in option.dataset) return;
        selectedIds.push(option.value);
        const price = parseFloat(option.dataset.price || '0');
        const feeVariantId = option.dataset.feeVariantId;
        if (feeVariantId && price > 0) {
          fees.push({
            optionId: option.value,
            feeVariantId,
            price,
            application: select.dataset.application || 'POR_PRENDA',
            title: (option.textContent || '').trim(),
          });
        }
      });
    });

    this.comboRules.forEach((rule) => {
      const allSelected = rule.comboOf.every((id) => selectedIds.includes(id));
      if (allSelected) {
        fees = fees.filter((f) => !rule.comboOf.includes(f.optionId));
        fees.push({
          optionId: rule.optionId,
          feeVariantId: rule.feeVariantId,
          price: rule.price,
          application: 'POR_PRENDA',
          title: rule.optionId.replace(/_/g, ' '),
        });
      }
    });

    if (this.logoFeeEl && this.setupFeeSelected() && this.hasPersonalization()) {
      const price = parseFloat(this.logoFeeEl.dataset.price || '0');
      const feeVariantId = this.logoFeeEl.dataset.feeVariantId;
      if (feeVariantId && price > 0) {
        fees.push({
          optionId: 'ADD_LOGO',
          feeVariantId,
          price,
          application: 'POR_PEDIDO',
          title: 'Logo Setup Fee',
        });
      }
    }

    return fees;
  }

  // Order-wide fees (the Logo Setup Fee) are charged once per cart. Remember which are
  // already there so they are neither re-added nor counted in the running total.
  async refreshCartOrderFees() {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      this.cartOrderFeeVariantIds = new Set(
        cart.items
          .filter((item) => {
            const props = item.properties || {};
            return props._Personalization_role === 'fee' && props._Personalization_application === 'POR_PEDIDO';
          })
          .map((item) => String(item.variant_id))
      );
    } catch (err) {
      // Keep the last known state; the fee is re-checked when adding to cart.
    }
  }

  // Selected fees minus order-wide ones the cart already carries.
  getNewFees() {
    return this.getSelectedFees().filter(
      (fee) => !(fee.application === 'POR_PEDIDO' && this.cartOrderFeeVariantIds.has(fee.feeVariantId.split('/').pop()))
    );
  }

  updateRunningTotal() {
    if (!this.runningTotalRow) return;
    const fees = this.getNewFees();
    const total = fees.reduce((sum, f) => sum + f.price, 0);
    if (total > 0) {
      this.runningTotalRow.hidden = false;
      this.runningTotalValue.textContent = `${this.currency}${total.toFixed(2)}`;
    } else {
      this.runningTotalRow.hidden = true;
    }
  }

  generateBundleId() {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    return 'bundle_' + Date.now() + '_' + Math.random().toString(36).slice(2, 10);
  }

  // Every selected position needs either its own artwork/text or a position to mirror.
  getIncompletePositions() {
    const incomplete = [];
    this.locations.forEach((location) => {
      if (location.sameAs) {
        if (!location.sameAsSelect.value) incomplete.push(location.position.label);
        return;
      }
      if (!location.hasFile && !location.textInput.value.trim()) {
        incomplete.push(location.position.label);
      }
    });
    return incomplete;
  }

  async handleAddToCart(event) {
    event.preventDefault();

    if (!this.form) {
      this.showSubmitError('Could not find the product form.');
      return;
    }

    const incomplete = this.getIncompletePositions();
    if (incomplete.length) {
      this.showSubmitError(`Add artwork or text for: ${incomplete.join(', ')}.`);
      return;
    }

    this.addToCartButton.disabled = true;
    const originalText = this.addToCartButton.textContent;
    this.addToCartButton.textContent = 'Adding…';
    this.submitError.hidden = true;

    const bundleId = this.generateBundleId();

    try {
      const formData = new FormData(this.form);
      // A position the shopper left on the text tab still submits an empty file input
      // (and vice versa); drop those so the cart only carries real values.
      Array.from(formData.entries()).forEach(([key, value]) => {
        if (!key.startsWith('properties[')) return;
        const isEmptyFile = value instanceof File && value.size === 0 && !value.name;
        if (value === '' || isEmptyFile) formData.delete(key);
      });
      // Tag the main product so fee lines can be visually grouped under it and
      // cascade-removed together in the cart.
      formData.append('properties[_Personalization_bundle_id]', bundleId);
      formData.append('properties[_Personalization_role]', 'main');
      // Marks products that need the shared order-wide setup fee, so the cart only drops
      // that fee together with the last such product.
      if (this.getSelectedFees().some((fee) => fee.application === 'POR_PEDIDO')) {
        formData.append('properties[_Personalization_logo_setup]', 'true');
      }

      // The cart may have changed since the page loaded (e.g. removed in the drawer).
      await this.refreshCartOrderFees();

      const mainResponse = await fetch('/cart/add.js', { method: 'POST', body: formData });
      if (!mainResponse.ok) {
        const err = await mainResponse.json().catch(() => ({}));
        throw new Error(err.description || 'Could not add the product to your cart.');
      }

      const mainQuantity = parseInt(formData.get('quantity') || '1', 10);
      const fees = this.getNewFees();

      for (const fee of fees) {
        const isOrderWide = fee.application === 'POR_PEDIDO';
        const quantity = isOrderWide ? 1 : mainQuantity;
        const numericId = fee.feeVariantId.split('/').pop();
        // An order-wide fee belongs to the cart, not to this product, so it gets the shared
        // bundle id "order" and no product title.
        const feeProperties = isOrderWide
          ? {
              _Personalization_bundle_id: 'order',
              _Personalization_role: 'fee',
              _Personalization_title: fee.title,
              _Personalization_application: fee.application,
            }
          : {
              _Personalization_bundle_id: bundleId,
              _Personalization_role: 'fee',
              _Personalization_product: this.productTitle,
              _Personalization_title: fee.title,
              _Personalization_application: fee.application,
            };
        const feeResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [
              {
                id: numericId,
                quantity,
                properties: feeProperties,
              },
            ],
          }),
        });
        if (!feeResponse.ok) {
          const err = await feeResponse.json().catch(() => ({}));
          throw new Error(err.description || 'The product was added, but a personalization fee failed to add. Please check your cart.');
        }
      }

      window.location.href = '/cart';
    } catch (err) {
      this.showSubmitError(err.message || 'Something went wrong adding this to your cart.');
      this.addToCartButton.disabled = false;
      this.addToCartButton.textContent = originalText;
    }
  }
}

customElements.define('product-customizer', ProductCustomizer);
